/**
 * Inline badges for the tool row.
 *
 * Edits / writes / multi-edits keep a numeric badge — the ``+N −N``
 * reading on a diff (or ``3 edits`` on a MultiEdit) is the entire
 * point and reads cleaner as a tabular tail than as prose. Persistent /
 * timeout / read-range stay in :func:`buildToolSummary`'s ``prose``
 * field as natural-language continuation.
 */

const META = 'shrink-0 select-none text-[11px] text-gray-400 tabular-nums dark:text-gray-500';

/**
 * Diff badge — ``-N +N`` for an Edit, ``+N`` for a Write. Returns
 * ``null`` for a no-op so the row doesn't render an empty tail
 * when the agent simply re-saved an unchanged file.
 */
export function DiffBadge({ added, removed }: { added?: number; removed?: number }) {
  const hasAdded = added !== undefined && added > 0;
  const hasRemoved = removed !== undefined && removed > 0;

  if (!hasAdded && !hasRemoved) return null;
  return (
    <span className="inline-flex shrink-0 select-none items-baseline gap-1 font-medium font-mono text-[12px] tabular-nums">
      {hasAdded && <span className="text-emerald-600/90 dark:text-emerald-400/90">+{added}</span>}
      {hasRemoved && <span className="text-red-600/90 dark:text-red-400/90">−{removed}</span>}
    </span>
  );
}

/** Edit count for a MultiEdit row — ``3 edits``. */
export function EditCountBadge({ count }: { count: number }) {
  return (
    <span className={META}>
      {count} edit{count === 1 ? '' : 's'}
    </span>
  );
}

/**
 * Subagent type tag — keeps rows like ``Ran subagent Explore
 * Test launch`` from reading as a doubled title. The human-facing
 * description stays in the sentence; the implementation type moves
 * into this quiet tag.
 */
export function SubagentTypeBadge({ label }: { label: string }) {
  return (
    <span className="relative -top-px inline-flex h-5 shrink-0 select-none items-center rounded-full border border-cyan-200/70 bg-cyan-50 px-2 font-medium text-[11px] text-cyan-700 capitalize leading-5 dark:border-cyan-400/20 dark:bg-cyan-400/10 dark:text-cyan-200">
      {label}
    </span>
  );
}
